import { useState, useEffect } from 'react'
import { useFetchData } from '../../hooks/useFetchData'
import { createTestSet } from '../../utils/shuffleSeed'
import { scoreMCQ } from '../../utils/evaluator'
import { Clock, CheckCircle, XCircle } from 'lucide-react'
import Question from './Question'

export default function TestRunner({ config, onComplete, onCancel, state, updateState }) {
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState({})
  const [timeLeft, setTimeLeft] = useState(config.duration * 60) // in seconds
  const [testData, setTestData] = useState(null)
  const [isComplete, setIsComplete] = useState(false)
  const [results, setResults] = useState(null)

  const currentModule = config.modules[currentModuleIndex]
  
  // Fetch data for current module
  const dataUrls = {
    oir: '/data/oir_questions.json',
    wat: '/data/wat_words.csv',
    tat: '/data/tat_images.json',
    srt: '/data/srt_questions.json',
  }

  const { data, loading } = useFetchData(dataUrls[currentModule])

  // Initialize test data
  useEffect(() => {
    if (data && !testData) {
      const questions = data.questions || data.situations || data.images || data
      const testSet = createTestSet(questions, config.questionCount)
      setTestData(testSet)
    }
  }, [data, config.questionCount])

  // Timer
  useEffect(() => {
    if (isComplete) return

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleComplete()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [isComplete])

  const handleAnswer = (questionId, answer) => {
    setAnswers({
      ...answers,
      [`${currentModule}-${questionId}`]: answer,
    })
  }

  const handleNext = () => {
    if (currentQuestionIndex < testData.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    } else if (currentModuleIndex < config.modules.length - 1) {
      setCurrentModuleIndex(currentModuleIndex + 1)
      setCurrentQuestionIndex(0)
      setTestData(null) // Reset for next module
    } else {
      handleComplete()
    }
  }

  const handleComplete = () => {
    const moduleResults = {}
    let totalQuestions = 0
    let correctAnswers = 0

    config.modules.forEach((module) => {
      const moduleAnswers = Object.entries(answers).filter(([key]) =>
        key.startsWith(`${module}-`)
      )
      moduleResults[module] = {
        attempted: moduleAnswers.length,
        answers: moduleAnswers,
      }
      totalQuestions += moduleAnswers.length
    })

    // Calculate score for OIR (MCQ only)
    if (config.modules.includes('oir')) {
      const oirData = data?.questions || []
      oirData.forEach((q) => {
        const answerKey = `oir-${q.id}`
        if (answers[answerKey] === q.answer) {
          correctAnswers++
        }
      })
    }

    const finalResults = {
      answers,
      moduleResults,
      totalQuestions,
      correctAnswers,
      totalScore: totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0,
      timeUsed: config.duration * 60 - timeLeft,
      seed: testData?.seed,
    }

    setResults(finalResults)
    setIsComplete(true)
    onComplete(finalResults)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <span className="ml-3">Loading test...</span>
      </div>
    )
  }

  if (isComplete && results) {
    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-slide-in">
        <div className="card bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-2">Test Completed!</h2>
            <p className="text-gray-600 dark:text-gray-400">
              You've successfully completed the test
            </p>
          </div>
        </div>

        <div className="card">
          <h3 className="text-xl font-bold mb-4">Your Results</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-3xl font-bold text-blue-600">{results.totalScore}%</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Score</div>
            </div>
            <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="text-3xl font-bold text-green-600">{results.correctAnswers}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Correct</div>
            </div>
            <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <div className="text-3xl font-bold text-orange-600">{results.totalQuestions}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total</div>
            </div>
            <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="text-3xl font-bold text-purple-600">
                {Math.floor(results.timeUsed / 60)}m
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Time Used</div>
            </div>
          </div>

          <div className="text-xs text-gray-500">
            Test Seed: {results.seed} (Save this to reproduce the same test)
          </div>
        </div>

        <button onClick={() => window.location.href = '/test'} className="btn-primary w-full">
          Back to Tests
        </button>
      </div>
    )
  }

  if (!testData || !testData.questions[currentQuestionIndex]) {
    return <div>Loading question...</div>
  }

  const currentQuestion = testData.questions[currentQuestionIndex]
  const progress = ((currentQuestionIndex + 1) / testData.questions.length) * 100
  const minutes = Math.floor(timeLeft / 60)
  const seconds = timeLeft % 60

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="card bg-primary-50 dark:bg-primary-900/20">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">{currentModule.toUpperCase()} Test</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Question {currentQuestionIndex + 1} of {testData.questions.length}
            </p>
          </div>
          <div className={`flex items-center text-2xl font-bold ${timeLeft < 300 ? 'text-red-600 animate-pulse' : ''}`}>
            <Clock className="w-6 h-6 mr-2" />
            {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
          </div>
        </div>

        <div className="progress-bar mt-4">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Question */}
      <Question
        question={currentQuestion}
        module={currentModule}
        answer={answers[`${currentModule}-${currentQuestion.id}`]}
        onAnswer={(answer) => handleAnswer(currentQuestion.id, answer)}
      />

      {/* Controls */}
      <div className="card">
        <div className="flex gap-3">
          <button onClick={onCancel} className="btn-secondary">
            Cancel Test
          </button>
          <button
            onClick={handleNext}
            className="btn-primary flex-1"
            disabled={!answers[`${currentModule}-${currentQuestion.id}`]}
          >
            {currentQuestionIndex === testData.questions.length - 1 &&
            currentModuleIndex === config.modules.length - 1
              ? 'Finish Test'
              : 'Next Question'}
          </button>
        </div>
      </div>
    </div>
  )
}
